import React, { useMemo, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import styles from './PlayerTable.module.css';
import { CSVLink } from "react-csv";
import { useTable, useSortBy } from 'react-table';
import { COLUMNS } from '../columns';
import PlayerModal from '../PlayerModal/PlayerModal';
import { jwtDecode } from 'jwt-decode';
import api from '../../api/axios';
import { LuDownload, LuRefreshCw } from 'react-icons/lu'
import { IoIosArrowUp, IoIosArrowDown, IoMdMail, IoMdPersonAdd } from "react-icons/io";
import { ConfirmationModal } from "../ConfirmationModal/ConfirmationModal.js"
import { LuCopy, LuCheck } from 'react-icons/lu';

export const PlayerTable = forwardRef(({ onPlayersUpdate }, ref) => {
  const [players, setPlayers] = useState([]);
  const [sports, setSports] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedSport, setSelectedSport] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddingPlayer, setIsAddingPlayer] = useState(false);
  const [isManualParse, setIsManualParse] = useState(false);
  const [importCooldownSeconds, setImportCooldownSeconds] = useState(0);
  const [pendingImportRefresh, setPendingImportRefresh] = useState(false);
  const [copiedPlayerId, setCopiedPlayerId] = useState(null);
  const [copyingPlayerId, setCopyingPlayerId] = useState(null);

  const columns = useMemo(() => COLUMNS, []);

  // CSV Headers
  const headers = [
      { label: "First Name", key: "first_name" },
      { label: "Last Name", key: "last_name" },
      { label: "Phone", key:"phone"},
      { label: "Email", key:"email"},
      { label: "Address", key: "address" },
      { label: "City", key: "city"},
      { label: "State", key: "state"},
      { label: "Sport", key: "sport" },
      { label: "Stars", key: "stars" },
      { label: "Jersey Number", key: "jersey_number" },
      { label: "Position", key: "position" },
      { label: "Secondary Position", key: "secondary_position" },
      { label: "Grad Year", key: "grad_year" },
      { label: "GPA", key: "gpa"},
      { label: "Club Team", key: "clubTeam" },
      { label: "High School", key: "high_school" },
      { label: "Club Team Coach Name", key: "club_team_coach_name" },
      { label: "Club Team Coach Email", key: "club_team_coach_email" },
      { label: "High School Coach Name", key: "high_school_coach_name" },
      { label: "High School Coach Email", key: "high_school_coach_email" },
      { label: "Parents Names", key: "parents_names" },
      { label: "Parents Contacts", key: "parents_contacts" },
  ];

  const getUserIdFromToken = () => {
    try {
      const token = localStorage.getItem("jwtToken")
      if (!token) {
        console.error("No JWT token found.");
        return null; // token missing
      }
      const decodedToken = jwtDecode(token);
      return decodedToken.user_id;
    } catch (error) {
      console.error("Error fetching JWT token", error);
      return null; // token missing 
    }
  }

  const refreshPlayerTable = async () => {
    await fetchSports();

    if (selectedSport === 'all') {
      await fetchPlayers();
    } else {
      await fetchPlayersBySport(selectedSport);
    }
  }

  // Expose refresh function to parent via ref
  useImperativeHandle(ref, () => ({
    refresh: refreshPlayerTable
  }));

  const fetchPlayers = async () => {
    try {
      const userId = getUserIdFromToken();
      const playerResponse = await api.get(`/players/${userId}`);  // fetch players under the user id

        setPlayers(playerResponse.data);
        onPlayersUpdate(playerResponse.data);
      } catch (error) {
      console.error("Error fetching players:", error);
    }
  };

  const fetchSports = async () => {
    try {
      const userId = getUserIdFromToken();
      const sportResponse = await api.get(`/users/${userId}/sports`);

     setSports(sportResponse.data || []) 
    } catch (error) {
      console.error("Issue fetching users sports", error);
    }
  }

  const fetchPlayersBySport = async (sportId) => {
    try {
      const userId = getUserIdFromToken();
      const playerResponse = await api.get(`/retrievePlayersBySport/${sportId}/${userId}`);

      setPlayers(playerResponse.data);
      onPlayersUpdate(playerResponse.data);
      
    } catch (error) {
      console.error("Error fetching players by sport:", error);
    }
  }

  const handleSportChange = async (e) => {
    try {
      const sportId = e.target.value; // store the current sport ID
      setSelectedSport(sportId);

      if (sportId === 'all') {
        await fetchPlayers();
      } else {
        await fetchPlayersBySport(sportId);
      }
    } catch (error) {
      console.error("Failure filter players by sport", error);
    }   
  }

  useEffect(() => {
    refreshPlayerTable();
  }, []);

  useEffect(() => {
    if (importCooldownSeconds <= 0) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setImportCooldownSeconds((currentSeconds) => currentSeconds - 1);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [importCooldownSeconds]);

  useEffect(() => {
    if (!pendingImportRefresh || importCooldownSeconds !== 0) {
      return undefined;
    }

    refreshPlayerTable();
    setPendingImportRefresh(false);
    return undefined;
  }, [importCooldownSeconds, pendingImportRefresh]);

  const savePlayer = async (updatedPlayer) => {
    try {
      const response = await api.put(
        `/playerupdate/${updatedPlayer.player_id}`,
        updatedPlayer
      );
      if (response.status === 200) {
        // Re-fetch to get updated data with JOINed position_name and sport_name
        refreshPlayerTable(); 
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error("Error updating player:", error);
    }
  };

  const addPlayer = async (newPlayerData) => {
    try {
      const token = localStorage.getItem("jwtToken");
      if (!token) {
        console.error("No token found. User is not authenticated.");
        return;
      }

      const decodedToken = jwtDecode(token);
      const username = decodedToken.username;
      const userResponse = await api.get(`/user/${username}`);
      const userId = userResponse.data.user_id;

      const newPlayer = {
        user_id: userId,
        sport_id: newPlayerData.sport_id,
        position_id: newPlayerData.position_id,
        first_name: newPlayerData.first_name || "",
        last_name: newPlayerData.last_name || "",
        email: newPlayerData.email || "",
        high_school: newPlayerData.high_school || "",
        state: newPlayerData.state || "",
      };

      const response = await api.post(`/newplayerform`, newPlayer);

      if (response.status === 201) {
        // Response contains only { message, player_id }, so re-fetch full rows instead of appending partial data.
        await refreshPlayerTable();
        setIsModalOpen(false);
      }

    } catch (error) {
      console.error(
        "Error adding player:",
        error.response?.data || error.message
      );
      console.error("Add player request details:", {
        status: error.response?.status,
        url: error.config?.url,
      });
    }
  };
  
  const deletePlayer = async (playerId) => {
    try {
      const response = await api.delete(`/playerdelete/${playerId}`);
      if (response.status === 200) {
        await refreshPlayerTable();
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error("Error deleting player:", error);
    }
  };

  const manualParse = async () => {
    if (importCooldownSeconds > 0) {
      return;
    }

    try {
      await api.post(`/trigger`)
      setPendingImportRefresh(true);
      setImportCooldownSeconds(30);
    } catch (error) {
      console.log("Error:", error)
    }
  };

  const handleCopyRecruitLink = async (event, playerId) => {
    event.stopPropagation();

    try {
      setCopyingPlayerId(playerId);
      const response = await api.get(`/players/${playerId}/token-link`);
      await navigator.clipboard.writeText(response.data.link);
      setCopiedPlayerId(playerId);
      setTimeout(() => {
        setCopiedPlayerId((currentPlayerId) => (
          currentPlayerId === playerId ? null : currentPlayerId
        ));
      }, 2000);
    } catch (error) {
      console.error('Error copying player update link:', error);
    } finally {
      setCopyingPlayerId(null);
    }
  };

  const tableInstance = useTable({ columns, data: players }, useSortBy);
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    
    <div className={styles.tableContainer}>
      {/* Header and buttons */}
      <div className={styles.header}>
        <h1>Recruits</h1>
        {/* Multi-sport select button */}
        <select className={styles.sportSelectButton} value={selectedSport} onChange={handleSportChange}>
          <option value="all">All</option>
          {sports.map((sport) => (
            <option className={styles.sportSelectOption} key={sport.sport_id} value={sport.sport_id}>
              {sport.sport_name}
            </option>
          ))}
        </select>

        <div className={styles.rightHeader}>        
          {/* Manual player addition button */}
          <button className={styles.buttonB}
            onClick={() => {
              setIsAddingPlayer(true);
              setSelectedPlayer(null);
              setIsModalOpen(true);
            }}
          >
            <IoMdPersonAdd className={styles.buttonIcons}/>
            Add Player
          </button>

          {/* Manual email parse button */}
          <button
            className={styles.buttonA}
            onClick={() => {setIsManualParse(true)}}
            disabled={importCooldownSeconds > 0}
          >
            <IoMdMail className={styles.buttonIcons}/>
            {importCooldownSeconds > 0
              ? `Import Players (${importCooldownSeconds}s)`
              : 'Import Players'}
          </button>
          {isManualParse && (
            <ConfirmationModal
              message="Are you sure you want to import players? Wait a few minutes after forwarding the email before using this feature."
              onConfirm={() => { manualParse(); setIsManualParse(false) }}
              onCancel={() => setIsManualParse(false)}
            />
          )}
        </div>
      </div>

      {/* Table */}
      <table className={styles.table} {...getTableProps()}>
        {/* Table Header */}
        <thead>
          {headerGroups.map((headerGroup) => {
            const { key: headerGroupKey, ...restHeaderGroupProps } =
              headerGroup.getHeaderGroupProps();
            return (
              <tr key={headerGroupKey} {...restHeaderGroupProps}>
                {headerGroup.headers.map((column) => {
                  const { key: headerKey, ...restHeaderProps } =
                    column.getHeaderProps(column.getSortByToggleProps());
                  return (
                    <th key={headerKey} {...restHeaderProps}>
                      {column.render("Header")}
                      <span>
                        {column.isSorted ? column.isSortedDesc
                            ? < IoIosArrowDown className={styles.sortIcon}/>
                            : < IoIosArrowUp className={styles.sortIcon}/>
                          : ""}
                      </span>
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        
        {/* Table Body */}
        <tbody className={styles.table} {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const { key: rowKey, ...restRowProps } = row.getRowProps();
            return (
              <tr
                key={rowKey}
                {...restRowProps}
                onClick={() => {
                  setSelectedPlayer(row.original);
                  setIsAddingPlayer(false);
                  setIsModalOpen(true);
                }}
                style={{ cursor: "pointer" }}
              >
                {row.cells.map((cell) => {
                  const { key: cellKey, ...restCellProps } = cell.getCellProps();
                  let value = cell.value;
                  if (value === null ||
                    value === "" ||
                    value === "N/A" ||
                    value?.toString().toLowerCase() === "none"
                  ) {
                    value = "-";
                  }
                  const isNameColumn = cell.column.id === "player_name"; // For styling name column

                  return (
                    <td key={cellKey} {...restCellProps}
                      className={isNameColumn ? styles.nameColumn : ""}
                    >
                      {isNameColumn ? (
                        <div className={styles.nameCell}>
                          <span className={styles.nameText}>
                            {value === "-" && cell.column.id !== "stars" ? "-" : cell.render('Cell')}
                          </span>
                          <button
                            type="button"
                            className={styles.copyLinkButton}
                            onClick={(event) => handleCopyRecruitLink(event, row.original.player_id)}
                            onMouseDown={(event) => event.stopPropagation()}
                            aria-label="Copy recruit update link"
                            title="Copy recruit update link"
                            disabled={copyingPlayerId === row.original.player_id}
                          >
                            {copiedPlayerId === row.original.player_id ? (
                              <LuCheck className={styles.copyIcon} />
                            ) : (
                              <LuCopy className={styles.copyIcon} />
                            )}
                          </button>
                        </div>
                      ) : (
                        value === "-" && cell.column.id !== "stars" ? "-" : cell.render('Cell')
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
        
      {/* Footer */}
      <div className={styles.header}>
        <CSVLink 
            headers={headers}
            data={players}
            filename="players.csv"
            target="_blank"
            style={{ textDecoration: "none" }}
        >
          <button className={styles.buttonB}>
            <LuDownload className={styles.buttonIcons} />
            Export to CSV
          </button>
        </CSVLink>
      </div>
      
      {/* Player statistics popup */}
      {isModalOpen && (
        <PlayerModal
          player={selectedPlayer}
          isAddingPlayer={isAddingPlayer}
          onClose={() => setIsModalOpen(false)}
          onSave={savePlayer}
          onAdd={addPlayer}
          onDelete={deletePlayer}
        />
      )}
    </div>
  );
});

PlayerTable.displayName = 'PlayerTable';
